import { ALWAYS_AVAILABLE_WINDOW_KEYS } from '../safeContext';
import { absorber, coercionStub } from './absorber';
import { getDocumentMirror } from './documentMirror';
import { getLocationMirror } from './locationMirror';
import { getNavigatorMirror } from './navigatorMirror';

// self.self.window.top... must all land back on this same mirror, or a chain like
// globalThis.window.localStorage would slip past the shadow on its second hop.
const CIRCULAR_KEYS = new Set([
  'self',
  'window',
  'globalThis',
  'top',
  'parent',
  'frames',
  'opener',
  'frameElement',
]);

function getUnifyAppsSettings(): unknown {
  try {
    if (typeof window === 'undefined') return undefined;
    return 'unifyAppsSettings' in window
      ? (window as unknown as Record<string, unknown>).unifyAppsSettings
      : undefined;
  } catch {
    return undefined;
  }
}

let cachedMirror: unknown = null;

// A Proxy, not an object literal: the get-trap must route every property we did not
// explicitly allow to the absorber, otherwise chains through unknown keys would come
// back as undefined and THROW one hop later ("window.fetch(...)"), turning silent
// absorption into a new error path.
export function getSafeWindowMirror(): unknown {
  if (cachedMirror) return cachedMirror;
  const mirror: unknown = new Proxy(
    {},
    {
      get(_target, prop) {
        const stub = coercionStub(prop);
        if (stub) return stub;
        if (typeof prop === 'string' && CIRCULAR_KEYS.has(prop)) return mirror;
        if (prop === 'location') return getLocationMirror();
        if (prop === 'navigator') return getNavigatorMirror();
        if (prop === 'document') return getDocumentMirror();
        if (prop === 'getUnifyAppsSettings') return getUnifyAppsSettings;
        // Native-webview bridges stay live in read-only too (shared list with
        // safeContext). Returning the real value — undefined when absent — keeps
        // typeof/!! presence checks truthful, where the absorber would read as a
        // (truthy) function and make every web page look like a native one.
        if (
          typeof prop === 'string' &&
          (ALWAYS_AVAILABLE_WINDOW_KEYS as readonly string[]).includes(prop)
        ) {
          if (typeof window === 'undefined') return undefined;
          return (window as unknown as Record<string, unknown>)[prop];
        }
        return absorber;
      },
      set() {
        return true;
      },
      deleteProperty() {
        return true;
      },
    },
  );
  cachedMirror = mirror;
  return mirror;
}
